import React from "react";
import { Route } from "react-router-dom";
import UsersPage from "./account/components/Users/UsersPage";
import RequestPage from "./support/RequestPage";
import CustomersPage from "./account/components/Customers/CustomersPage";
import ProfilePage from "./account/components/Profile/ProfilePage";
import NotificationsV2Page from "./account/components/NotificationsV2/NotificationsV2Page";
import GroupFormPage from "./account/components/NotificationsV2/GroupForm/GroupFormPage";
import ApiKeysPage from "./account/components/Integrations/ApiKeysPage";
import MainAuditLogs from "./support/auditLogs/MainAuditLogs";
import AuditLogDetailPage from "./support/auditLogs/AuditLogDetailPage";
import TagsManagerPage from "./account/components/TagsManager/TagsManagerPage";
import TagFormPage from "./account/components/TagsManager/TagForm/TagFormPage";
import { RequireTenantType } from "@shared/components/RequireTenantType";
import { getNotificationsAllowedTenantTypes } from "@shared/navigation/SideMenu/sideMenuVisibility";
import {
  isCustomerApiEnabled,
  isTagsManagerEnabled,
} from "@shared/config/featureFlags";

// Gated behind the Tags Manager flag so direct URL access can't bypass the
// hidden menu entry. Kept in sync with the sidemenu gate
// (`filterAccountAreaItems`). When the flag is off the legacy tag flow
// is used and these routes are not registered.
const tagsManagerRoutes = isTagsManagerEnabled()
  ? [
      <Route key="tags-manager" path="/account/tags-manager" element={<TagsManagerPage />} />,
      <Route key="tags-manager-new" path="/account/tags-manager/new" element={<TagFormPage mode="create" />} />,
      <Route
        key="tags-manager-edit"
        path="/account/tags-manager/:tagId"
        element={<TagFormPage mode="edit" />}
      />,
    ]
  : [];

// Gated behind the same predicate as the sidemenu row, so the hidden entry
// can't be reached by typing `/account/api`. Registering it unconditionally is
// what left the previous `/account/integrations` path reachable in production
// for a year (CTX-37) — the menu was gated and the route was not, and nothing
// tied the two together.
const customerApiRoutes = isCustomerApiEnabled()
  ? [<Route key="api" path="/account/api" element={<ApiKeysPage />} />]
  : [];

// Same allow-list is enforced by the sidemenu gate
// (`filterAccountAreaItems`) so direct URL access can't bypass the rule.
// Computed once at module load — env vars are static for the lifetime of the app.
const NOTIFICATIONS_ALLOWED_TENANT_TYPES = getNotificationsAllowedTenantTypes();

const notificationsRoutes = [
    <Route
        key="notifications-group-new"
        path="/account/notifications/groups/new"
        element={
            <RequireTenantType allowedTypes={NOTIFICATIONS_ALLOWED_TENANT_TYPES}>
                <GroupFormPage mode="create" />
            </RequireTenantType>
        }
    />,
    <Route
        key="notifications-group-edit"
        path="/account/notifications/groups/:groupId"
        element={
            <RequireTenantType allowedTypes={NOTIFICATIONS_ALLOWED_TENANT_TYPES}>
                <GroupFormPage mode="edit" />
            </RequireTenantType>
        }
    />,
    <Route
        key="notifications-v2"
        path="/account/notifications"
        element={
            <RequireTenantType allowedTypes={NOTIFICATIONS_ALLOWED_TENANT_TYPES}>
                <NotificationsV2Page />
            </RequireTenantType>
        }
    />,
];

export const settingsRoutes = [
    <Route key="profile" path="/account/profile" element={<ProfilePage />} />,
    <Route key="users" path="/account/users" element={<UsersPage />} />,
    <Route key="request" path="/support/request" element={<RequestPage />} />,
    <Route key="customers" path="/account/customers" element={<CustomersPage />} />,
    ...notificationsRoutes,
    ...tagsManagerRoutes,
    ...customerApiRoutes,
    // Audit Logs (Support → cross-product). Both pages self-gate via
    // <RequireTenantType allowedTypes={["GLOBAL_ADMIN"]}>.
    <Route key="audit-logs" path="/support/audit-logs" element={<MainAuditLogs />} />,
    <Route key="audit-log-detail" path="/support/audit-logs/:id" element={<AuditLogDetailPage />} />,
];
